import React, { useMemo } from 'react';
import AddCardForm from 'components/stripe/AddCardForm';
import ButtonComponent from 'components/button/buttonComponent';
import {
  SubscriptionFormData,
  useCreateSubscriptionMethod,
} from 'hooks/useStripeConnect';
import { useForm } from 'react-hook-form';
import { useGetPage } from 'hooks/useGetPage';
import { pageIds } from 'utilities/constants';
import { useAuth } from 'auth/context/AuthContext';
import { SUBSCRIPTION_ACTIVE_STATUS } from 'auth/context/AuthProvider';

interface AddPayMethodFormProps {
  setErrorMessage: (message: string) => void;
  submitTitle?: string;
  callback?: (date?: string) => void;
}

const AddPayMethodForm: React.FC<AddPayMethodFormProps> = ({
  setErrorMessage,
  submitTitle,
  callback = () => ({}),
}) => {
  const { data: locale } = useGetPage({
    locale: 'en',
    pageId: pageIds.MEMBERSHIP,
  });
  const { stripeSubscription } = useAuth();
  const { onSubmit, processing } = useCreateSubscriptionMethod(
    setErrorMessage,
    callback,
  );

  const {
    handleSubmit,
    setError,
    clearErrors,
    reset,
    formState: { errors },
  } = useForm<SubscriptionFormData>({
    mode: 'onChange',
  });

  const hasCurrentSubscriptionEnd = useMemo(
    () =>
      !SUBSCRIPTION_ACTIVE_STATUS.includes(
        stripeSubscription?.[0]?.status as number,
      ),
    [stripeSubscription],
  );

  const handleStripeElementChange = (
    name: string,
    error?: Record<string, string>,
  ) => {
    if (error) {
      setError(name as 'name', { type: 'manual', message: error.message });
    } else {
      clearErrors(name as 'name');
    }
  };

  return (
    <form
      className="flex flex-col w-full gap-4"
      onSubmit={handleSubmit(() => onSubmit().then(() => reset()))}
    >
      <AddCardForm
        errors={errors as Record<string, Record<string, string>>}
        onElementChange={handleStripeElementChange}
      />
      <ButtonComponent
        className="w-full mt-5"
        htmlButtonType="submit"
        testID="active"
        disabled={processing}
        showSpinner={processing}
      >
        {hasCurrentSubscriptionEnd
          ? submitTitle || locale?.activateSubscription
          : 'Add new card'}
      </ButtonComponent>
    </form>
  );
};

export default AddPayMethodForm;
